import Image from "next/image";
import type { PortableTextBlock } from "next-sanity";

import cn from "classnames";

import { RichText } from "@/components/richText/richText.component";

import {
  overlayClass,
  textAlignClass,
  textColorClass,
} from "./healthHero.utils";

export function HealthHero({ block }: { block: HealthHeroBlock }) {
  const desktopUrl = block.backgroundImage?.url;
  const mobileUrl = block.mobileBackgroundImage?.url;

  if (!desktopUrl && !mobileUrl) return null;
  if (!block.title?.length) return null;

  const colorClass = textColorClass(block.textColor);
  const alignClass = textAlignClass(block.textAlign);
  const scrimClass = overlayClass(block.overlay);

  return (
    <section
      data-testid="health-hero"
      className={cn(
        "relative isolate flex min-h-[min(36rem,85svh)] w-full items-center overflow-hidden sm:min-h-[min(40rem,80svh)] lg:min-h-[min(44rem,75vh)]",
        colorClass,
      )}>
      {mobileUrl ? (
        <Image
          src={mobileUrl}
          alt={
            block.mobileBackgroundImage?.alt ?? block.backgroundImage?.alt ?? ""
          }
          fill
          priority
          sizes="100vw"
          className={cn("-z-20 object-cover", { "lg:hidden": desktopUrl })}
        />
      ) : null}
      {desktopUrl ? (
        <Image
          src={desktopUrl}
          alt={block.backgroundImage?.alt ?? ""}
          fill
          priority
          sizes="100vw"
          className={cn("-z-20 object-cover", { "max-lg:hidden": mobileUrl })}
        />
      ) : null}

      {scrimClass ? (
        <div aria-hidden className={cn("absolute inset-0 -z-10", scrimClass)} />
      ) : null}

      <div className="relative container mx-auto flex w-full justify-center px-6 py-20 sm:px-10 sm:py-24 lg:px-14 lg:py-28">
        <div
          className={cn(
            "flex w-full max-w-4xl flex-col gap-4 sm:gap-5",
            alignClass,
          )}>
          {block.eyebrow?.length ? (
            <div
              data-testid="health-hero-eyebrow"
              className={cn(
                "w-full text-[11px] font-bold tracking-[0.14em] uppercase sm:text-xs",
                "[&_.underline]:underline-offset-4 [&_p]:m-0 [&_u]:underline-offset-4",
                colorClass,
              )}>
              <RichText>{block.eyebrow}</RichText>
            </div>
          ) : null}

          <div
            data-testid="health-hero-title"
            className={cn(
              "w-full [&_h1]:m-0 [&_h2]:m-0 [&_h3]:m-0 [&_p]:m-0",
              colorClass,
            )}>
            <RichText asH1={block.asH1}>{block.title}</RichText>
          </div>
        </div>
      </div>
    </section>
  );
}

export type HealthHeroBlock = {
  _type: "healthHero";
  _key: string;
  backgroundImage?: {
    url?: string | null;
    alt?: string | null;
  } | null;
  mobileBackgroundImage?: {
    url?: string | null;
    alt?: string | null;
  } | null;
  eyebrow?: PortableTextBlock[] | null;
  title?: PortableTextBlock[] | null;
  asH1?: boolean | null;
  textColor?: string | null;
  textAlign?: string | null;
  overlay?: string | null;
};
